import { pipe, isEmpty, trim, toString, find } from 'lodash/fp';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import minMax from 'dayjs/plugin/minMax';
import objectSupport from 'dayjs/plugin/objectSupport';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { LANGUAGE, AddressType } from 'types';
import { isNull, isUndefined, isObject } from 'lodash';
dayjs.extend(utc);
dayjs.extend(objectSupport);
dayjs.extend(minMax);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

const now = dayjs().utc().tz();

export const isEmptyStr = pipe(toString, trim, isEmpty);

export const toLanguageType = (language: string = 'en'): LANGUAGE => {
  switch (language) {
    case 'cn':
      return LANGUAGE.CN;
    default:
      return LANGUAGE.EN;
  }
};

export const isInValidEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return !re.test(email);
};

export const isNumberOnly = (value: string) => {
  const re = /^\d*\.?\d*$/;
  return re.test(value);
};

export const isObjectEmpty = (obj: any) =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const isObjectPropertiesEmpty = (obj: Object | any) => {
  const res = Object.values(obj).indexOf('') >= 0;
  return res;
};

export const isNumeric = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

export const toLocalDateTimeFormat = (val: any) => {
  const date = !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().tz().format('DD/MM/YYYY h:mm a').toString()
    : '';
  return date;
};

export const toLocalDateTimeFormatVariant = (val: any) => {
  const date = !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().tz().format('D MMM YYYY h.mm a').toString()
    : '';
  return date;
};

export const toLocalDateTimeFormatWithSeconds = (val: any) => {
  const date = !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().tz().format('DD/MM/YYYY h:mm:ss a').toString()
    : '';
  return date;
};

export const toLocalDateFormat = (val: any) =>
  !(isNull(val) || isUndefined(val))
    ? dayjs(val).utc().tz().format('DD/MM/YYYY').toString()
    : '';

export const toDateAPIFormat = (val: any) => dayjs(val).format('YYYY-MM-DD');

export const toDateFormat = (val: any) =>
  !(isNull(val) || isUndefined(val)) && dayjs(val).utc().format('DD/MM/YYYY');

export const toDateFormatFullMonth = (val: any) =>
  !(isNull(val) || isUndefined(val)) && dayjs(val).utc().format('DD MMMM YYYY');

export const toTitleCase = (value: string) =>
  value &&
  value.replace(/\w\S*/g, (txt) => {
    if (txt.indexOf("'") === 1) {
      return `${txt.charAt(0).toUpperCase()}'${txt.charAt(2).toUpperCase()}${txt
        .substr(3)
        .toLowerCase()}`;
    } else if (txt.toLowerCase().indexOf('mc') === 0) {
      return `Mc${txt.charAt(2).toUpperCase()}${txt.substr(3).toLowerCase()}`;
    }
    return `${txt.charAt(0).toUpperCase()}${txt.substr(1).toLowerCase()}`;
  });

export const convertStringToDateFormat = (
  date: string | Date,
  format: string
) => (isObject(date) ? dayjs(date).format(format) : date);

export const debounce = (func: Function, wait = 50) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  return function (...args: Parameters<any>) {
    const doLater = function () {
      timeoutId = undefined;
      func(args);
    };

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(doLater, wait);
  };
};

export const getFullAddress = (address: AddressType) => {
  const { line1, line2, locality, postcode, state } = address;
  return `
          ${line1 ? toTitleCase(line1) : ''}${
    line2 ? `, ${toTitleCase(line2)}` : ''
  }, 
          ${locality ? toTitleCase(locality) : ''} ${state ? state : ''}
          ${postcode ? postcode : ''}
          `;
};

export const formFullAddress = (address: Array<any>) => {
  const arr = address.filter((a) => a !== 'AUS');
  const divider = arr.length - 3;
  const formedAddress = arr.reduce(function (str, value, idx) {
    if (!value.trim()) {
      return str;
    }

    if (idx >= divider) {
      return `${str}${value.trim()} `;
    }

    return `${str}${value.trim()}, `;
  }, '');

  return formedAddress.trim();
};

/*
 * Params
 * extDays = no of days to be extended. For ex: 28 days
 * 17/12/2020[ExtendedDate] and 16/12/2020[selectedDate] = true
 */
export const isDateAfterExtDate = (selectedDate: any, extDays: number) => {
  const extendedDate = dayjs().tz().add(extDays, 'day');
  return !extendedDate.isAfter(selectedDate, 'date');
};
/*
 * Params
 * extDays = no of days to be extended. For ex: # Months
 * Month (January as 1, December as 12)
 */
export const isDateAfterExtMonth = (selectedDate: any, extMonths: number) => {
  const extendedDate = now.add(extMonths, 'month').subtract(1, 'day');
  return !extendedDate.isAfter(selectedDate, 'date');
};
export const isSelectedTodayDate = (date: any) => {
  return now.isSame(date, 'date');
};

export const isAfterTodayDate = (date: any) => {
  const endOfYesterday = now.subtract(1, 'day').endOf('day');
  return endOfYesterday.isBefore(date);
};

export const getDateAfterMonths = (extMonths: number) => {
  return now.add(extMonths, 'month').subtract(1, 'day').utc().tz();
};

export const getDateAfterDays = (days: number) => {
  return now.add(days, 'day');
};

export const getDateBeforeDays = (days: number) => {
  return now.subtract(days, 'day');
};

export const getNewDate = (date: any = undefined) => {
  return dayjs(date).tz();
};

export const getNewDateWithTimezone = (
  date: any = undefined,
  timezone: string | undefined = undefined
) => {
  return dayjs(date).tz(timezone);
};

export const getTodayDate = () => Utils.toLocalDateFormat(now);

export const getTimeZone = () => dayjs.tz.guess();

export const getDaysFromNow = (val: any) => {
  return now.diff(val, 'day');
};

export const getDaysUntil = (val: any, precise: boolean = false) => {
  const endDate = dayjs(val);

  // Standard mode (dayjs default behavior)
  if (!precise) {
    return endDate.diff(now, 'day');
  }

  // Precise mode
  if (endDate.isBefore(now)) {
    // For past dates: return negative, ensuring any elapsed time counts as a full day
    return -Math.ceil(now.diff(endDate, 'hour') / 24);
  } else {
    // For future dates: only count complete days remaining
    return Math.floor(endDate.diff(now, 'hour') / 24);
  }
};

export const getDiffDates = (date1: any, date2: any) => {
  return dayjs(date1).diff(dayjs(date2));
};

export const findOjectKeyVal = (obj: Object, findObj: any) =>
  find(obj, findObj);

export const roleMapper = (role: string) => {
  switch (role.toUpperCase()) {
    case 'DBYDADMIN':
      return 'BYDA Administrator';
    case 'ENQUIRER':
      return 'Enquirer';
    case 'SYSADMIN':
      return 'Support';
    case 'AOADMIN':
      return 'Asset owner';
    default:
      return null;
  }
};

export const UNKNOWN_ERROR = 'Unknown error';

export const validURL = (str: string) => {
  var pattern = new RegExp(
    '^(https://www.|https://)?[a-z0-9]+([-.]{1}[a-z0-9]+)*.[a-z]{2,5}(:[0-9]{1,5})?(/.*)?$'
  ); // fragment locator
  return !!pattern.test(str);
};

export const downloadFile = (
  blobData: BlobPart,
  fileName: string,
  mime: string = 'application/octet-stream'
) => {
  const blob = new Blob([blobData], { type: mime });
  const blobURL = window.URL.createObjectURL(blob);
  const tempLink = document.createElement('a');
  tempLink.style.display = 'none';
  tempLink.href = blobURL;
  tempLink.setAttribute('download', fileName);

  // Safari thinks _blank anchor are pop ups. We only want to set _blank
  // target if the browser does not support the HTML5 download attribute.
  // This allows you to download files in desktop safari if pop up blocking
  // is enabled.
  if (typeof tempLink.download === 'undefined') {
    tempLink.setAttribute('target', '_blank');
  }

  document.body.appendChild(tempLink);
  tempLink.click();

  setTimeout(() => {
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(blobURL);
  }, 200);
};

export const upperFirst = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const translateEnquiryStatus = (
  status: string,
  archivedAt: string,
  isIgnore: boolean = false,
  longTranslation: boolean = false
) => {
  let translatedStatus = status;

  if (isIgnore) {
    return 'Free plan limit reached';
  }

  switch (status) {
    case 'created':
      translatedStatus = 'Created';
      break;
    case 'waiting':
      translatedStatus = longTranslation ? 'Waiting for responses' : 'Waiting';
      break;
    case 'fulfilled':
      translatedStatus = 'All received';
      break;
    case 'expired':
      translatedStatus = 'Expired enquiry';
      break;
    case 'failed':
      translatedStatus = longTranslation
        ? 'Enquiry failed to be created'
        : 'Failed';
      break;
    default:
      translatedStatus = status;
      break;
  }

  if (archivedAt) {
    return process.env.REACT_APP_SENTINEL_RESPONSE_COLLATION === 'true'
      ? 'Archived'
      : `${translatedStatus} (Archived)`;
  }

  return translatedStatus;
};

export const convertShapesToMultiPolygon = (shapes: GeoJSON.Polygon[]) => {
  const coordinates: GeoJSON.Position[][][] = shapes.reduce(
    (results: GeoJSON.Position[][][], shape: GeoJSON.Polygon) => {
      return [...results, shape.coordinates];
    },
    []
  );

  return {
    type: 'MultiPolygon',
    coordinates
  };
};

export const middleTruncate = (string: string, length: number) => {
  const mark = '\u2026';
  const position = Math.floor(length) / 2 - 1;

  const markOffset = mark.length;
  const minLength = 4;

  let str = string;

  if (typeof str === 'string') {
    str = str.trim();
  }

  const invalid =
    typeof str !== 'string' ||
    str.length < minLength ||
    typeof length !== 'number' ||
    length <= minLength ||
    length >= str.length - markOffset;

  if (invalid) return string;

  if (position >= length - markOffset) {
    const start = str.substring(0, length - markOffset);
    return `${start}${mark}`;
  }

  const start = str.substring(0, position);
  const end = str.slice(position + markOffset - length);

  return `${start}${mark}${end}`;
};

export const checkIfExeFile = (file: File) => {
  return new Promise((resolve) => {
    const blob = file;
    const fileReader = new FileReader();

    fileReader.readAsArrayBuffer(blob);
    fileReader.onloadend = function (e: any) {
      const arr = new Uint8Array(e.target.result).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }
      const res = checkFileType(file, header);
      resolve(res);
    };
  });
};

const checkFileType = (file: File, fileHeader: any) => {
  let notExeFile = true;
  const { name: fileName, type: fileType } = file;
  const fileNameSplit = fileName.split('.');
  const fileExtension =
    fileNameSplit.length > 0
      ? fileNameSplit[fileNameSplit.length - 1]
      : 'unknown';
  if (
    fileType === 'application/x-msdownload' ||
    fileType === 'application/vnd.microsoft.portable-executable' ||
    fileExtension === 'exe' ||
    fileHeader === '4d5a900'
  ) {
    notExeFile = false;
  }
  return notExeFile;
};

const Utils = {
  convertShapesToMultiPolygon,
  debounce,
  isEmptyStr,
  toLanguageType,
  isInValidEmail,
  isNumberOnly,
  isObjectEmpty,
  isObjectPropertiesEmpty,
  isNumeric,
  toLocalDateTimeFormat,
  toLocalDateFormat,
  toTitleCase,
  convertStringToDateFormat,
  getFullAddress,
  isDateAfterExtDate,
  isSelectedTodayDate,
  isAfterTodayDate,
  getDateAfterMonths,
  getTodayDate,
  isDateAfterExtMonth,
  getDateAfterDays,
  getDateBeforeDays,
  getNewDate,
  getNewDateWithTimezone,
  getTimeZone,
  getDaysFromNow,
  findOjectKeyVal,
  roleMapper,
  getDiffDates,
  UNKNOWN_ERROR,
  toDateFormat,
  toDateAPIFormat,
  validURL,
  downloadFile,
  upperFirst,
  middleTruncate,
  translateEnquiryStatus,
  checkIfExeFile
};

export default Utils;
